import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import { Alert, Button, Center, Group, Loader, Paper, Select, SimpleGrid, Stack, Table, Text, Title } from "@mantine/core";
import { IconAlertCircle, IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { collection, collectionGroup, doc, getDoc, getDocs, limit, onSnapshot, orderBy, query, Timestamp, where } from "firebase/firestore";
import { Margin, usePDF } from "react-to-pdf";
import * as XLSX from "xlsx";
import { MyUserDataContext } from "../../contexts";
import { useFirestore } from "../../helpers/firebaseContext";
import useFirestoreCollection from "../../helpers/useFirestoreCollection";
import { getOrderExportValue, orderExportHeaders } from "../../helpers/orderExportHeaders";
import { AddressFromFinder, CustomClaims, Fund, KYC, Order, OrderStatusCode, PoliticallyExposedType, RMUser } from "../../types";

const LAST_SELECTED_FUND_KEY = "openEndedFundProgress:lastFund";
type Campaign = NonNullable<Fund["fundraising_campaign"]>[number];
type CampaignCustomer = { id: string; name: string; civilID: string; segment: string };
type SegmentRow = { segment: string; clients: number; customers: CampaignCustomer[]; subscriptions: number; redemptions: number; total: number };
type CampaignOrderExport = { order: Order; eligibility: "Eligible" | "Partially eligible" | "Not eligible"; reason: string };
type RMScoreRow = { email: string; name: string; rmID: string; segment: string; mobile: string; clients: number; sales: number; points: number };

const numberValue = (value: unknown) => {
    const normalized = typeof value === "string" ? value.replace(/,/g, "") : value;
    const number = typeof normalized === "number" ? normalized : Number(normalized);
    return Number.isFinite(number) ? Math.abs(number) : 0;
};
const orderDate = (order: Order) => order.created;
const orderAmount = (order: Order) => numberValue(order.postNavAmount);
const customerId = (order: Order) => order.customerObj?.id || order.customerObj?.civilID || order.customerObj?.mobile || order.id || "unknown";
const formatAmount = (value: number, currency?: string) => `${new Intl.NumberFormat("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 }).format(value)}${currency ? ` ${currency}` : ""}`;
const formatDate = (value: Timestamp) => value.toDate().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const campaignKey = (campaign: Campaign) => `${campaign.start_date.toMillis()}-${campaign.end_date.toMillis()}`;

const OpenEndedFundProgress = () => {
    const db = useFirestore();
    const context = useContext(MyUserDataContext);
    const claims = context.firebaseTokenResult?.claims.customClaims as CustomClaims;
    const restrictedRM = !!claims?.kfh_oper && !claims?.admin && !claims?.cr;
    const fundsQuery = query(collection(db, "funds"), where("type", "==", "open_ended"));
    const { data: fundsData, loading: fundsLoading } = useFirestoreCollection(fundsQuery, true);
    const rmsQuery = query(collection(db, "rms"));
    const { data: rmsData, loading: rmsLoading } = useFirestoreCollection(rmsQuery, true);
    const funds = useMemo(() => ((fundsData as Fund[]) || []).filter((fund) => fund.fundraising_campaign?.length), [fundsData]);
    const [fundId, setFundId] = useState<string | null>(null);
    const [selectedCampaignKey, setSelectedCampaignKey] = useState<string | null>(null);
    const [orders, setOrders] = useState<Order[]>([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const [ordersError, setOrdersError] = useState<string | null>(null);
    const [expandedSegments, setExpandedSegments] = useState<Set<string>>(new Set());
    const [excelLoading, setExcelLoading] = useState(false);
    const [customerRMEmails, setCustomerRMEmails] = useState<Map<string, string>>(new Map());
    const [customerSegments, setCustomerSegments] = useState<Map<string, string>>(new Map());
    const [customerRMsLoading, setCustomerRMsLoading] = useState(false);
    const { toPDF, targetRef } = usePDF({ filename: "open_ended_fundraising_progress.pdf", page: { margin: Margin.MEDIUM } });

    useEffect(() => {
        if (!funds.length) return;
        setFundId((current) => {
            if (current && funds.some((fund) => fund.id === current)) return current;
            const saved = localStorage.getItem(LAST_SELECTED_FUND_KEY);
            return saved && funds.some((fund) => fund.id === saved) ? saved : funds[0].id;
        });
    }, [funds]);
    useEffect(() => { if (fundId) localStorage.setItem(LAST_SELECTED_FUND_KEY, fundId); }, [fundId]);

    const fund = useMemo(() => funds.find((item) => item.id === fundId), [funds, fundId]);
    const campaigns = useMemo(() => [...(fund?.fundraising_campaign || [])].sort((a, b) => b.start_date.toMillis() - a.start_date.toMillis()), [fund]);
    useEffect(() => {
        setSelectedCampaignKey((current) => current && campaigns.some((item) => campaignKey(item) === current) ? current : campaigns[0] ? campaignKey(campaigns[0]) : null);
    }, [campaigns]);
    const campaign = useMemo(() => campaigns.find((item) => campaignKey(item) === selectedCampaignKey), [campaigns, selectedCampaignKey]);

    useEffect(() => setExpandedSegments(new Set()), [fundId, selectedCampaignKey]);

    useEffect(() => {
        if (!fundId || !campaign) {
            setOrders([]);
            setOrdersLoading(false);
            return;
        }
        setOrdersLoading(true);
        setOrdersError(null);
        const campaignOrdersQuery = query(
            collectionGroup(db, "orders"),
            where("fund.id", "==", fundId),
            where("created", ">=", campaign.start_date),
            where("created", "<=", campaign.end_date),
        );
        return onSnapshot(campaignOrdersQuery, (snapshot) => {
            setOrders(snapshot.docs.map((document) => ({ ...(document.data() as Order), id: (document.data() as Order).id || document.id })));
            setOrdersLoading(false);
        }, (error) => {
            console.error("Error fetching open-ended campaign orders", error);
            setOrders([]);
            setOrdersLoading(false);
            setOrdersError("Could not load fund orders right now.");
        });
    }, [db, fundId, campaign]);

    useEffect(() => {
        const customerIDs = Array.from(new Set(orders.map((order) => order.customerObj?.id).filter((id): id is string => !!id)));
        if (!customerIDs.length) {
            setCustomerRMEmails(new Map());
            setCustomerSegments(new Map());
            setCustomerRMsLoading(false);
            return;
        }
        let cancelled = false;
        setCustomerRMsLoading(true);
        Promise.all(customerIDs.map(async (id) => {
            try {
                const customerSnapshot = await getDoc(doc(db, "customers", id));
                const customer = customerSnapshot.data();
                return {
                    id,
                    rmEmail: `${customer?.rm_email || ""}`.trim().toLowerCase(),
                    segment: `${customer?.segment || ""}`.trim(),
                };
            } catch (error) {
                console.error(`Could not load assigned RM for customer ${id}`, error);
                return { id, rmEmail: "", segment: "" };
            }
        })).then((customers) => {
            if (!cancelled) {
                setCustomerRMEmails(new Map(customers.map((customer) => [customer.id, customer.rmEmail])));
                setCustomerSegments(new Map(customers.map((customer) => [customer.id, customer.segment])));
            }
        }).finally(() => {
            if (!cancelled) setCustomerRMsLoading(false);
        });
        return () => { cancelled = true; };
    }, [db, orders]);

    const rmsByEmail = useMemo(() => {
        const result = new Map<string, RMUser>();
        ((rmsData as RMUser[]) || []).forEach((rm) => {
            const email = `${rm.email || rm.id || ""}`.trim().toLowerCase();
            if (email) result.set(email, rm);
        });
        return result;
    }, [rmsData]);

    const report = useMemo(() => {
        const empty = { rows: [] as SegmentRow[], scoreRows: [] as RMScoreRow[], exportOrders: [] as CampaignOrderExport[], clients: 0, subscriptions: 0, redemptions: 0, total: 0, subscriptionOrders: 0, redemptionOrders: 0 };
        if (!campaign) return empty;
        const currentCustomerSegment = (order: Order) =>
            customerSegments.get(order.customerObj?.id || "") || "Unspecified";
        const start = campaign.start_date.toMillis();
        const end = campaign.end_date.toMillis();
        const campaignSegments = new Set(campaign.segments.map((item) => item.trim()).filter(Boolean));
        const eligibleOrders = orders.filter((order) => {
            const created = orderDate(order)?.toMillis();
            const clientSegment = currentCustomerSegment(order);
            return created !== undefined && created >= start && created <= end
                && order.tags?.orderStatus?.code === OrderStatusCode.done
                && (order.type === "subscription" || order.type === "redemption")
                && (clientSegment === "Unspecified" || campaignSegments.has(clientSegment))
                && (clientSegment === "Unspecified" || !restrictedRM || !claims?.segment || clientSegment === claims.segment);
        }).sort((a, b) => (orderDate(a)?.toMillis() || 0) - (orderDate(b)?.toMillis() || 0));

        // Redemptions consume only the customer's subscriptions created during this campaign.
        const availableByCustomer = new Map<string, number>();
        const bySegment = new Map<string, { customers: Map<string, CampaignCustomer>; subscriptions: number; redemptions: number }>();
        const clients = new Set<string>();
        const exportOrders: CampaignOrderExport[] = [];
        const rmSales = new Map<string, { clients: Set<string>; sales: number }>();
        let subscriptions = 0, redemptions = 0, subscriptionOrders = 0, redemptionOrders = 0;
        eligibleOrders.forEach((order) => {
            const amount = orderAmount(order);
            if (!amount) {
                exportOrders.push({ order, eligibility: "Not eligible", reason: "Missing or zero Post NAV Amount" });
                return;
            }
            const client = customerId(order);
            const orderSegment = currentCustomerSegment(order);
            const row = bySegment.get(orderSegment) || { customers: new Map<string, CampaignCustomer>(), subscriptions: 0, redemptions: 0 };
            const campaignCustomer = {
                id: client,
                name: order.customerObj?.name_en || order.customerObj?.name_ar || "--",
                civilID: order.customerObj?.civilID || "--",
                segment: orderSegment,
            };
            const assignedRMEmail = customerRMEmails.get(order.customerObj?.id || "") || "";
            const addRMSales = (amount: number) => {
                const key = assignedRMEmail || "__unassigned__";
                const rmRow = rmSales.get(key) || { clients: new Set<string>(), sales: 0 };
                rmRow.clients.add(client);
                rmRow.sales += amount;
                rmSales.set(key, rmRow);
            };
            if (order.type === "subscription") {
                exportOrders.push({ order, eligibility: "Eligible", reason: "" });
                clients.add(client);
                row.customers.set(client, campaignCustomer);
                subscriptions += amount;
                addRMSales(amount);
                subscriptionOrders += 1;
                row.subscriptions += amount;
                availableByCustomer.set(client, (availableByCustomer.get(client) || 0) + amount);
            } else {
                const available = availableByCustomer.get(client) || 0;
                const eligibleRedemption = Math.min(amount, available);
                if (!eligibleRedemption) {
                    exportOrders.push({ order, eligibility: "Not eligible", reason: "No remaining eligible subscription amount for this client in the campaign" });
                    return;
                }
                if (eligibleRedemption < amount) {
                    exportOrders.push({
                        order,
                        eligibility: "Partially eligible",
                        reason: `Only ${eligibleRedemption} of ${amount} is eligible; the redemption exceeds the client's remaining campaign subscriptions`,
                    });
                } else {
                    exportOrders.push({ order, eligibility: "Eligible", reason: "" });
                }
                clients.add(client);
                row.customers.set(client, campaignCustomer);
                redemptions += eligibleRedemption;
                addRMSales(-eligibleRedemption);
                row.redemptions += eligibleRedemption;
                availableByCustomer.set(client, available - eligibleRedemption);
                redemptionOrders += 1;
            }
            bySegment.set(orderSegment, row);
        });
        const rows = Array.from(bySegment.entries()).map(([name, data]) => ({
            segment: name,
            clients: data.customers.size,
            customers: Array.from(data.customers.values()).sort((a, b) => a.name.localeCompare(b.name)),
            subscriptions: data.subscriptions,
            redemptions: data.redemptions, total: data.subscriptions - data.redemptions,
        })).sort((a, b) => b.total - a.total);
        const pointsPerAmount = numberValue(campaign.rm_points_per_amount);
        const scoreRows = Array.from(rmSales.entries()).map(([email, data]) => {
            const rm = email === "__unassigned__" ? undefined : rmsByEmail.get(email);
            const sales = Math.max(data.sales, 0);
            return {
                email: rm?.email || (email === "__unassigned__" ? "Unassigned" : email),
                name: rm?.name_en || (email === "__unassigned__" ? "Unassigned" : "--"),
                rmID: rm?.rm_id || "--",
                segment: rm?.segment || "--",
                mobile: rm?.mobile || "--",
                clients: data.clients.size,
                sales,
                points: email === "__unassigned__" || !pointsPerAmount ? 0 : Math.floor(sales / pointsPerAmount),
            };
        }).sort((a, b) => b.points - a.points || b.sales - a.sales || a.name.localeCompare(b.name));
        return { rows, scoreRows, exportOrders, clients: clients.size, subscriptions, redemptions, total: subscriptions - redemptions, subscriptionOrders, redemptionOrders };
    }, [orders, campaign, restrictedRM, claims?.segment, customerRMEmails, customerSegments, rmsByEmail]);

    const exportExcel = async () => {
        if (!fund || !campaign || excelLoading) return;
        setExcelLoading(true);
        try {
            const rmsSnapshot = await getDocs(collection(db, "rms"));
            const rmsByEmail = new Map<string, RMUser>();
            rmsSnapshot.docs.forEach((rmDocument) => {
                const rm = rmDocument.data() as RMUser;
                const email = (rm.email || rmDocument.id).trim().toLowerCase();
                if (email) rmsByEmail.set(email, { ...rm, email: rm.email || rmDocument.id, id: rm.id || rmDocument.id });
            });

            const exportOrders = await Promise.all(report.exportOrders.map(async ({ order: sourceOrder, eligibility, reason }) => {
                const exported: Record<string, any> = { ...sourceOrder };
                const id = sourceOrder.id;
                const customerID = sourceOrder.customerObj?.id;
                const customerRef = customerID ? doc(db, "customers", customerID) : null;
                const [customerSnapshot, kycSnapshot, actionsSnapshot] = await Promise.all([
                    customerRef ? getDoc(customerRef) : null,
                    customerRef ? getDocs(query(collection(customerRef, "kyc"), orderBy("created", "desc"), where("state.status_code", "==", "valid"), limit(1))) : null,
                    customerID && id ? getDoc(doc(db, `customers/${customerID}/orders/${id}/private/actions`)) : null,
                ]);

                const customerData = customerSnapshot?.data();
                if (customerData) {
                    const finderAddress = customerData.address_from_finder as AddressFromFinder | undefined;
                    if (finderAddress) {
                        let address = "";
                        if (finderAddress.BlockEnglish) address += `Block:${finderAddress.BlockEnglish}, `;
                        if (finderAddress.StreetEnglish) address += `St:${finderAddress.StreetEnglish.replace("St ", "")}, `;
                        if (finderAddress.HouseEnglish) address += `H:${finderAddress.HouseEnglish}, `;
                        if (finderAddress.NeighborhoodEnglish) address += `${finderAddress.NeighborhoodEnglish}, `;
                        customerData.address = `${address}Kuwait`;
                    }
                    if (customerData.passportData?.expiryDate?.toDate) {
                        customerData.passportData = { ...customerData.passportData, expiryDate: customerData.passportData.expiryDate.toDate().toLocaleDateString("en-GB") };
                    }
                    exported.customerObj = customerData;
                    const customerRMEmail = `${customerData.rm_email || ""}`.trim().toLowerCase();
                    exported.clientRM = customerRMEmail ? rmsByEmail.get(customerRMEmail) : undefined;
                }

                const kycData = kycSnapshot?.docs[0]?.data() as KYC | undefined;
                if (kycData) {
                    exported.kyc = { ...kycData };
                    if (Array.isArray(exported.kyc.sourcesOfIncome)) exported.kyc.sourcesOfIncome = exported.kyc.sourcesOfIncome.join(",");
                    if (Array.isArray(exported.kyc.sourcesofWealth)) exported.kyc.sourcesofWealth = exported.kyc.sourcesofWealth.join(",");
                    if (Array.isArray(exported.kyc.clientNature)) exported.clientClassification = exported.kyc.clientNature.join(",");
                    exported.powerOfAttorney = kycData.powerOfAttorney ? "Yes" : "No";
                    exported.politicallyExposed = kycData.politicallyExposedType
                        ? (kycData.politicallyExposedType === PoliticallyExposedType.None ? "No" : `Yes (${kycData.politicallyExposedType})`)
                        : (kycData.politicallyExposed ? "Yes" : "No");
                    if (kycData.politicallyExposed || (kycData.politicallyExposedType && kycData.politicallyExposedType !== PoliticallyExposedType.None)) {
                        exported.pepReason = kycData.politicalPosition || "";
                        if (kycData.relativePoliticalPosition) exported.pepReason += ` - Relative: ${kycData.relativePoliticalPosition}`;
                    }
                    if (kycData.state?.risk_code === "high") exported.highRiskReason = kycData.state.risk;
                    exported.kyc.risk_code = kycData.state?.risk_code;
                }

                const approvals = actionsSnapshot?.data()?.approvals || [];
                exported.approvalDate = approvals.find((item: any) => item.type === "approve")?.date?.toDate?.().toLocaleDateString("en-GB");
                exported.reviewDate = approvals.find((item: any) => item.type === "review")?.date?.toDate?.().toLocaleString("en-GB");
                exported.createdDate = sourceOrder.created?.toDate().toLocaleDateString("en-GB");
                exported.created = sourceOrder.created?.toDate().toLocaleString("en-GB");
                exported.nav_date = sourceOrder.nav_date?.toDate().toLocaleDateString("en-GB");
                exported.transferDate = sourceOrder.transferDate?.toDate().toLocaleDateString("en-GB");
                exported.referredBy = sourceOrder.referredBy || sourceOrder.rm;
                exported.submittedBy = sourceOrder.submittedBy || sourceOrder.rm;
                exported.campaignEligibility = eligibility;
                exported.campaignNotEligibleReason = reason;
                return exported;
            }));

            const campaignHeaders = [
                ...orderExportHeaders,
                { label: "Campaign Eligibility", key: "campaignEligibility" },
                { label: "Not Eligible Reason", key: "campaignNotEligibleReason" },
                { label: "Client RM Mobile", key: "clientRM.mobile" },
                { label: "Client RM Name", key: "clientRM.name_en" },
                { label: "Client RM ID", key: "clientRM.rm_id" },
                { label: "Client RM Segment", key: "clientRM.segment" },
                { label: "Client RM Email", key: "clientRM.email" },
            ];
            const rows = exportOrders.map((order) => campaignHeaders.map((header) => getOrderExportValue(order, header.key)));
            const worksheet = XLSX.utils.aoa_to_sheet([campaignHeaders.map((header) => header.label), ...rows]);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
            const safeFundName = (fund.name_en || fund.name || fund.id).replace(/[^a-z0-9_-]+/gi, "_");
            const startDate = campaign.start_date.toDate().toISOString().slice(0, 10);
            const endDate = campaign.end_date.toDate().toISOString().slice(0, 10);
            XLSX.writeFile(workbook, `${safeFundName}_${startDate}_${endDate}_orders.xlsx`, { bookType: "xlsx" });
        } finally {
            setExcelLoading(false);
        }
    };

    const exportScoreboardExcel = () => {
        if (!fund || !campaign || customerRMsLoading || rmsLoading) return;
        const rows = report.scoreRows.map((row, index) => ({
            Rank: row.name === "Unassigned" ? "--" : index + 1,
            RM: row.name,
            "RM ID": row.rmID,
            Segment: row.segment,
            Email: row.email,
            Mobile: row.mobile,
            "Clients (#)": row.clients,
            "Net eligible sales": row.sales,
            Points: row.points,
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows.length ? rows : [{
            Rank: "", RM: "", "RM ID": "", Segment: "", Email: "", Mobile: "",
            "Clients (#)": "", "Net eligible sales": "", Points: "",
        }]);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "RM Scoreboard");
        const safeFundName = (fund.name_en || fund.name || fund.id).replace(/[^a-z0-9_-]+/gi, "_");
        const startDate = campaign.start_date.toDate().toISOString().slice(0, 10);
        const endDate = campaign.end_date.toDate().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `${safeFundName}_${startDate}_${endDate}_rm_scoreboard.xlsx`, { bookType: "xlsx" });
    };

    if (fundsLoading) return <Center py="xl"><Loader /></Center>;
    if (!funds.length) return <Alert color="yellow" icon={<IconAlertCircle size={16} />} title="No open-ended fundraising campaigns">Add a fundraising_campaign period to an open-ended fund in Firestore to show it here.</Alert>;

    const toggleSegment = (segmentName: string) => {
        setExpandedSegments((current) => {
            const next = new Set(current);
            if (next.has(segmentName)) next.delete(segmentName);
            else next.add(segmentName);
            return next;
        });
    };

    return <Stack gap="md" style={{ width: "100%" }}>
        <Group justify="space-between" align="flex-end">
            <div><Title order={3}>{fund?.name_en || "Open-ended fundraising performance"}</Title><Text c="dimmed" size="sm">Campaign sales are subscriptions minus eligible redemptions.</Text></div>
            <Group align="flex-end">
                <Select w={280} label="Fund" value={fundId} onChange={setFundId} data={funds.map((item) => ({ value: item.id, label: item.name_en || item.name || item.id }))} />
                <Select w={300} label="Campaign period" value={selectedCampaignKey} onChange={setSelectedCampaignKey} data={campaigns.map((item) => ({ value: campaignKey(item), label: `${formatDate(item.start_date)} - ${formatDate(item.end_date)}` }))} />
                <Button variant="default" onClick={exportExcel} disabled={ordersLoading || !campaign} loading={excelLoading}>Download Excel</Button>
                <Button onClick={() => toPDF()}>Download PDF</Button>
            </Group>
        </Group>
        {ordersError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{ordersError}</Alert>}
        <Stack ref={targetRef} gap="md" style={{ width: "100%" }}>
            {campaign && <Text size="sm" fw={600}>{formatDate(campaign.start_date)} - {formatDate(campaign.end_date)}</Text>}
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
                {[["Clients (#)", `${report.clients}`], [`Subscriptions (${report.subscriptionOrders})`, formatAmount(report.subscriptions, fund?.currency)], [`Eligible redemptions (${report.redemptionOrders})`, formatAmount(report.redemptions, fund?.currency)], ["Net sales", formatAmount(report.total, fund?.currency)]].map(([label, value]) => <Paper key={label} p="md" shadow="sm" radius="md"><Text c="dimmed" fw={600} size="xs">{label}</Text><Title order={2}>{value}</Title></Paper>)}
            </SimpleGrid>
            {ordersLoading ? <Center><Loader /></Center> : <Paper shadow="sm" radius="md" p="md">
                <Title order={4} mb="xs">Sales by segment</Title>
                <Table striped highlightOnHover withRowBorders={false}>
                    <Table.Thead><Table.Tr><Table.Th>Segment</Table.Th><Table.Th>Clients (#)</Table.Th><Table.Th>Subscriptions</Table.Th><Table.Th>Eligible redemptions</Table.Th><Table.Th>Net sales</Table.Th></Table.Tr></Table.Thead>
                    <Table.Tbody>
                        {report.rows.length ? report.rows.map((row) => {
                            const expanded = expandedSegments.has(row.segment);
                            return <Fragment key={row.segment}>
                                <Table.Tr>
                                    <Table.Td>
                                        <Button
                                            variant="subtle"
                                            color="dark"
                                            size="compact-sm"
                                            px={4}
                                            leftSection={expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
                                            onClick={() => toggleSegment(row.segment)}
                                        >
                                            {row.segment}
                                        </Button>
                                    </Table.Td>
                                    <Table.Td>{row.clients}</Table.Td>
                                    <Table.Td>{formatAmount(row.subscriptions, fund?.currency)}</Table.Td>
                                    <Table.Td>{formatAmount(row.redemptions, fund?.currency)}</Table.Td>
                                    <Table.Td><Text fw={700}>{formatAmount(row.total, fund?.currency)}</Text></Table.Td>
                                </Table.Tr>
                                {expanded && <Table.Tr>
                                    <Table.Td colSpan={5} p="md">
                                        <Table withTableBorder withColumnBorders>
                                            <Table.Thead><Table.Tr><Table.Th>Customer name</Table.Th><Table.Th>Civil ID</Table.Th><Table.Th>Segment</Table.Th></Table.Tr></Table.Thead>
                                            <Table.Tbody>{row.customers.map((customer) => <Table.Tr key={customer.id}><Table.Td>{customer.name}</Table.Td><Table.Td>{customer.civilID}</Table.Td><Table.Td>{customer.segment}</Table.Td></Table.Tr>)}</Table.Tbody>
                                        </Table>
                                    </Table.Td>
                                </Table.Tr>}
                            </Fragment>;
                        }) : <Table.Tr><Table.Td colSpan={5}><Text c="dimmed">No orders in this campaign period.</Text></Table.Td></Table.Tr>}
                        <Table.Tr><Table.Td><Text fw={700}>Total</Text></Table.Td><Table.Td>{report.clients}</Table.Td><Table.Td>{formatAmount(report.subscriptions, fund?.currency)}</Table.Td><Table.Td>{formatAmount(report.redemptions, fund?.currency)}</Table.Td><Table.Td><Text fw={700}>{formatAmount(report.total, fund?.currency)}</Text></Table.Td></Table.Tr>
                    </Table.Tbody>
                </Table>
            </Paper>}
            <Paper shadow="sm" radius="md" p="md">
                <Group justify="space-between" mb="xs">
                    <Title order={4}>RM points scoreboard</Title>
                    <Group>
                        <Text size="sm" c="dimmed">
                            1 point per {formatAmount(numberValue(campaign?.rm_points_per_amount), fund?.currency)} of net eligible sales
                        </Text>
                        <Button variant="default" size="xs" onClick={exportScoreboardExcel} disabled={customerRMsLoading || rmsLoading || !campaign}>
                            Download scoreboard Excel
                        </Button>
                    </Group>
                </Group>
                {customerRMsLoading || rmsLoading ? <Center py="md"><Loader size="sm" /></Center> : <Table striped highlightOnHover withRowBorders={false}>
                    <Table.Thead><Table.Tr><Table.Th>Rank</Table.Th><Table.Th>RM</Table.Th><Table.Th>RM ID</Table.Th><Table.Th>Segment</Table.Th><Table.Th>Email</Table.Th><Table.Th>Mobile</Table.Th><Table.Th>Clients (#)</Table.Th><Table.Th>Net eligible sales</Table.Th><Table.Th>Points</Table.Th></Table.Tr></Table.Thead>
                    <Table.Tbody>
                        {report.scoreRows.length ? report.scoreRows.map((row, index) => <Table.Tr key={row.email}><Table.Td>{row.name === "Unassigned" ? "--" : index + 1}</Table.Td><Table.Td><Text fw={600}>{row.name}</Text></Table.Td><Table.Td>{row.rmID}</Table.Td><Table.Td>{row.segment}</Table.Td><Table.Td>{row.email}</Table.Td><Table.Td>{row.mobile}</Table.Td><Table.Td>{row.clients}</Table.Td><Table.Td>{formatAmount(row.sales, fund?.currency)}</Table.Td><Table.Td><Text fw={700}>{row.points}</Text></Table.Td></Table.Tr>) : <Table.Tr><Table.Td colSpan={9}><Text c="dimmed">No eligible RM sales in this campaign.</Text></Table.Td></Table.Tr>}
                    </Table.Tbody>
                </Table>}
            </Paper>
            <Alert color="blue" icon={<IconAlertCircle size={16} />}>A redemption counts only up to that client's remaining subscriptions created during this campaign. Pre-campaign holdings are excluded.</Alert>
        </Stack>
    </Stack>;
};

export default OpenEndedFundProgress;
